import { wrap } from "module";
import React, { useState, CSSProperties, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { formatValue } from "../utils/formatNumber";
import { Theme } from "../theme/colors";

export interface DataRow {
  [key: string]: string | number | boolean;
}

type PaginatedTableProps = {
  theme: Theme;
  data: DataRow[] | [];
};

const PaginatedTable: React.FC<PaginatedTableProps> = ({ theme, data }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null
  );

  // Memoized computation for filtered and sorted data
  const processedData = useMemo(() => {
    let filteredData = data;

    // Apply filters
    if (Object.keys(filters).length > 0) {
      filteredData = filteredData.filter((row) =>
        Object.entries(filters).every(([key, value]) => {
          if (!value) return true; // Ignore empty filters
          const cellValue = row[key];
          return cellValue
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        })
      );
    }

    // Apply sorting
    if (sortColumn) {
      filteredData = [...filteredData].sort((a, b) => {
        const aValue = a[sortColumn] === "" ? null : a[sortColumn];
        const bValue = b[sortColumn] === "" ? null : b[sortColumn];
    
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1; // Move null/empty to end
        if (bValue == null) return -1; // Move null/empty to end
    
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sortDirection === "asc"
            ? aValue.toString().localeCompare(bValue.toString())
            : bValue.toString().localeCompare(aValue.toString());
        }
      });
    }    

    return filteredData;
  }, [data, filters, sortColumn, sortDirection]);

  const totalPages = Math.ceil(processedData.length / rowsPerPage);
  const displayedData = processedData.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(0);
  };

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      // Toggle sort direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New column selected, default to ascending order
      setSortColumn(columnKey);
      setSortDirection("asc");
    }
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  const handleFilterIconClick = (key: string) => {
    setActiveFilterColumn((prev) => (prev === key ? null : key));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    setCurrentPage(0); // Reset to first page when filters change
  };

  const cellFormatter = (value: string | number | boolean, key: string) => {
    if (typeof value === 'number') {
      if ((key.toLowerCase().includes("year") && value >= 2000 && value <= 2030) ||
          (key.toLowerCase().includes("month") && value >= 1 && value <= 12) ||
          (key.toLowerCase().includes("day") && value >= 1 && value <= 31) ||
          key.toLowerCase().includes("_id") ||
          key.toLowerCase().includes("-id")) {
        return value.toFixed(0);
      } else if (value < 100) {
        return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      } else {
        return value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }
    }
    if (typeof value === 'string' && /^\d+\.\d+$/.test(value)) {
      return parseFloat(value).toFixed(2);
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) {
      return value.split("T")[0];
    }
    return value;
  };
  const isNotMobile = useMediaQuery({ minWidth: 455 });

  const commonCellStyle: CSSProperties = {
    textAlign: "left",
    border: theme === 'dark' ? "1px solid #191919" : "1px solid #DDDDDD",
    padding: "5px",
    position: "relative",
  };

  const headerCellStyle: CSSProperties = {
    ...commonCellStyle,
    backgroundColor: theme === 'dark' ? "#191919" : "#F0F0F0",
    color: theme === 'dark' ? "#FFFFFF" : "#000000",
  };

  const trStyles: CSSProperties = {
    border: theme === 'dark' ? "1px solid #191919" : "1px solid #DDDDDD",
  };


  const getCellStyle = (value: number | string | boolean): CSSProperties => ({
    ...commonCellStyle,
    textAlign: typeof value === "number" ? "right" : "left",
  });

  // Simple filter icon (you can replace this with an actual icon)
  const FilterIcon = () => (
    <span style={{ marginLeft: "5px", cursor: "pointer" }}>🔍</span>
  );

  // Function to handle CSV export
  const handleExportCSV = () => {
    // Convert processedData to CSV format
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(","), // Header row
      ...processedData.map((row) =>
        headers
          .map((fieldName) => {
            const cellValue = row[fieldName];
            if (cellValue == null) {
              return "";
            }
            // Escape double quotes by doubling them, and wrap in double quotes if needed
            const formattedValue = cellValue.toString().replace(/"/g, '""');
            if (formattedValue.includes(",") || formattedValue.includes('"')) {
              return `"${formattedValue}"`;
            }
            return formattedValue;
          })
          .join(",")
      ),
    ].join("\n");

    // Create a Blob from the CSV string
    const blob = new Blob([csvRows], { type: "text/csv;charset=utf-8;" });

    // Create a link to trigger the download
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "table_data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ padding: "9px", minWidth: 0 }}>
      <div style={{ overflowX: "auto", width: "100%", minWidth: 0 }}>
        <table style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {data.length > 0 &&
                Object.keys(data[0]).map((key) => (
                  <th
                    key={key}
                    style={{
                      ...headerCellStyle,
                      textAlign: "center",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span
                        onClick={() => handleSort(key)}
                        style={{ flexGrow: 1 }}
                      >
                        {key}
                        {sortColumn === key &&
                          (sortDirection === "asc" ? " ▲" : " ▼")}
                      </span>
                    </div>
                    {activeFilterColumn === key && (
                      <input
                        type="text"
                        value={filters[key] || ""}
                        onChange={(e) =>
                          handleFilterChange(key, e.target.value)
                        }
                        style={{
                          width: "100%",
                          marginTop: "5px",
                        }}
                      />
                    )}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {displayedData.map((row, rowIndex) => (
              <tr key={rowIndex} style={trStyles}>
                {Object.entries(row).map(([key, value], colIndex) => (
                  <td
                    key={colIndex}
                    style={{ ...getCellStyle(value), whiteSpace: "nowrap" }}
                  >
                    {cellFormatter(value, key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        {totalPages > 1 && (
          <div style={{
            display: "flex",
            flexDirection: isNotMobile ? "row" : "column",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "0.75rem"
          }}>
            <div>
              <button onClick={handlePrevious} disabled={currentPage === 0}>
                Previous
              </button>
              <span style={{ margin: "0 10px", }}>
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={handleNext}
                disabled={currentPage === totalPages - 1}
              >
                Next
              </button>
            </div>
            <div>
              <label style={{ marginLeft: "10px", display: "flex", gap: "0.5rem" }}>
                <span >
                  Rows per page:
                </span>
                <select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                  {[5, 10, 15, 20].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div>
              <button onClick={handleExportCSV}>
                Download CSV
              </button>
            </div>
          </div>
        )}
        {totalPages <= 1 && (
          <div>
            <button onClick={handleExportCSV}>
              Download CSV
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaginatedTable;
